import { useEvidenceSubmarkingMutation } from "@/features/case-evaluation/api/queries/evidence-query";
import type { CaseEvidence, CaseEvidenceForm } from "@/features/case-evaluation/types/evidence";
import { useDebounceEffect } from "@/hooks/use-debounce-effect";
import { canvasPreview } from "@/utils/canvas-preview";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import ReactCrop, { type Crop, type PixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import CaseEvidenceEditModal from "@/features/case-evaluation/components/case-evidence-edit-modal";
import type { Media } from "@/features/dashboard/types/media";
import { IconArrowsMaximize, IconCrop, IconWriting, IconX } from "@tabler/icons-react";
import { v4 as uuidv4 } from "uuid";
import ConfirmWithPasswordModal from "../confirm-with-password-modal";
import { Button } from "../core/buttons/button";
import { useModal } from "../core/modals/use-modal";
import { ScrollArea } from "../core/scroller";
import Tooltip from "../core/tooltip";
import CustomTldraw from "./custom-tldraw";

export function ImageViewerWithAddEvidence({
	src,
	alt,
	evidenceData,
	setDraftSubmarkings,
	handleSetImage,
	closeDrawing,
	handleDrawMode,
	newExhibit,
	isDrawMode,
	isCropMode,
	handleEvidence,
	handleCancelled,
	isLoading,
}: {
	src?: string;
	alt?: string;
	evidenceData: CaseEvidence;
	setDraftSubmarkings?: (values: CaseEvidence[]) => void;
	handleSetImage?: (img: Media) => void;
	closeDrawing: boolean;
	isDrawMode: boolean;
	isCropMode: boolean;
	handleDrawMode: (values: boolean) => void;
	newExhibit?: string;
	handleEvidence: (values: CaseEvidence) => void;
	handleCancelled: (values: boolean) => void;
	isLoading: boolean;
}) {
	const previewCanvasRef = useRef<HTMLCanvasElement>(null);
	const imgRef = useRef<HTMLImageElement>(null);
	const blobUrlRef = useRef("");
	const [crop, setCrop] = useState<Crop>();
	const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
	const [activateCropMode, setCropMode] = useState<boolean>(false);
	const [activateDrawMode, setDrawMode] = useState<boolean>(closeDrawing || false);
	const caseEvidenceMutation = useEvidenceSubmarkingMutation();
	const modal = useModal();
	const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
	const [fullscreen, setFullscreen] = useState(false);

	const storedTime = sessionStorage.getItem("password_entered_at");

	const handleAddEvidenceSubmit = async (values: CaseEvidenceForm) => {
		const expirationTime = moment(storedTime).add(5, "minutes");
		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			await caseEvidenceMutation.mutateAsync(values);
		} else {
			try {
				modal.openModal({
					allowClose: true,
					title: "Confirm Submit",
					description: "Are you sure? This action cannot be undone.",
					size: "md",
					showHeader: true,
					showFooter: false,
					component: (
						<ConfirmWithPasswordModal
							onSubmit={async () => {
								await caseEvidenceMutation.mutateAsync(values);
								setCropMode(false);
								setDrawMode(false);
							}}
						/>
					),
				});
			} catch (error) {
				console.log(error);
			}
		}
	};

	const evidences = {
		case_id: evidenceData.case_id,
		parties: evidenceData.parties,
		evidence_type: evidenceData.evidence_type,
		case_record_user_id: evidenceData?.case_record_user_id,
	};

	const handleAddEvidence = async (type: string) => {
		const image = imgRef.current;
		const previewCanvas = previewCanvasRef.current;
		if (!image || !previewCanvas || !completedCrop) {
			throw new Error("Crop canvas does not exist");
		}

		const scaleX = image.naturalWidth / image.width;
		const scaleY = image.naturalHeight / image.height;

		const offscreen = new OffscreenCanvas(completedCrop.width * scaleX, completedCrop.height * scaleY);

		const ctx = offscreen.getContext("2d");
		if (!ctx) {
			throw new Error("No 2d context");
		}

		ctx.drawImage(previewCanvas, 0, 0, previewCanvas.width, previewCanvas.height, 0, 0, offscreen.width, offscreen.height);

		const blob = await offscreen.convertToBlob({
			type: "image/png",
		});

		if (blobUrlRef.current) {
			URL.revokeObjectURL(blobUrlRef.current);
		}
		blobUrlRef.current = URL.createObjectURL(blob);

		const file = new File([blob], uuidv4(), { type: "image/png" });

		if (type === "submarking") {
			modal.openModal({
				title: "Add Submarking",
				description: `Add Submarking Evidence of Exhibit ${evidenceData?.exhibit}`,
				component: (
					<CaseEvidenceEditModal file={file} url={blobUrlRef.current} data={undefined} newSubmarking={evidenceData} onSubmit={handleAddEvidenceSubmit} />
				),
			});
		} else {
			modal.openModal({
				title: "Add Evidence",
				description: "Add Evidence of Exhibit",
				component: (
					<CaseEvidenceEditModal
						requiredEvidence={evidences}
						file={file}
						url={blobUrlRef.current}
						data={undefined}
						submarking={undefined}
						onSubmit={handleAddEvidenceSubmit}
					/>
				),
			});
		}
	};

	useEffect(() => {
		if (!completedCrop || !imgRef.current) return;

		const img = imgRef.current;
		const rect = img.getBoundingClientRect();

		const scaleX = img.naturalWidth / img.width;
		const scaleY = img.naturalHeight / img.height;

		const cropX = completedCrop.x + scaleX;
		const cropY = completedCrop.y + scaleY;

		const left = rect.left + cropX;
		const top = rect.top + cropY;

		setButtonPosition({
			top: top - 40,
			left: left,
		});
	}, [completedCrop]);

	useDebounceEffect(
		async () => {
			if (completedCrop?.width && completedCrop?.height && imgRef.current && previewCanvasRef.current) {
				canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop, 1);
			}
		},
		100,
		[completedCrop],
	);

	//on submit set draw mode to false
	useEffect(() => {
		if (closeDrawing !== null) {
			setDrawMode(false);
			setFullscreen(false);
		}
	}, [closeDrawing]);

	useEffect(() => {
		handleDrawMode(activateDrawMode);
	}, [handleDrawMode, setDrawMode, activateDrawMode, isDrawMode]);

	useEffect(() => {
		if (isDrawMode) {
			setDrawMode(true);
		}
	}, [isDrawMode]);

	return (
		<div className="flex flex-col w-full bg-background overflow-y-hidden">
			<div className="flex flex-row w-full h-[40px] px-4 border-b border-slate-200">
				<div className="flex flex-row w-full items-center">
					<span className="text-base font-bold text-foreground-dark">Image Preview</span>
				</div>
				<div className="flex flex-row items-center w-full">
					<div className="flex flex-row justify-end w-full gap-1">
						{!activateCropMode && !activateDrawMode && (
							<Tooltip content="Present Full Screen" side="bottom" className="z-[99]">
								<Button onClick={() => setFullscreen(true)} type="button" size="icon" variant="ghost">
									<IconArrowsMaximize size={18} stroke={1.5} />
								</Button>
							</Tooltip>
						)}
					</div>
				</div>
			</div>
			<ScrollArea>
				<div className="">
					{activateCropMode && (
						<div className="flex flex-col items-center w-full">
							{!!src && (
								<ReactCrop
									crop={crop}
									onChange={(_, percentCrop) => setCrop(percentCrop)}
									onComplete={(c) => setCompletedCrop(c)}
									aspect={undefined}
									minHeight={100}
								>
									<img ref={imgRef} alt={alt} src={src} />
								</ReactCrop>
							)}
							{!!completedCrop && (
								<>
									<canvas
										ref={previewCanvasRef}
										style={{
											visibility: "hidden",
										}}
									/>
									<div
										className="flex flex-row justify-between"
										style={{
											position: "absolute",
											top: `${buttonPosition.top}px`,
											left: `${buttonPosition.left}px`,
											zIndex: 1000,
										}}
									>
										{/* <Button size="sm" type="button" onClick={() => handleAddEvidence("main")}>
										Add as Evidence
									</Button> */}
										<Button size="sm" type="button" onClick={() => handleAddEvidence("submarking")}>
											Add as Submarking
										</Button>
									</div>
								</>
							)}
						</div>
					)}

					{!activateCropMode && !activateDrawMode && (
						<div className="flex flex-col bg-slate-100 w-full items-center">
							<img alt={alt} src={src} className="h-[900px] p-8" />
						</div>
					)}

					{activateDrawMode && (
						<div className="flex flex-col items-center w-full">
							<div className="col-span-12 w-full py-1">
								<div
									style={{
										position: "fixed",
										top: 120,
										left: "20.5%",
										width: "80%",
										height: "85%",
										border: "1px solid #ccc",
										backgroundColor: "#fff",
										overflow: "hidden",
									}}
								>
									<CustomTldraw
										src={src}
										data={evidenceData}
										onImgUpdate={handleSetImage}
										onBulkEvidenceSubmit={setDraftSubmarkings}
										setIsCancelled={(value: boolean) => {
											if (value === true) {
												setDrawMode(false);
												handleCancelled(true);
											}
										}}
										newExhibit={newExhibit}
										isFullScreen={fullscreen}
										handleEvidence={handleEvidence}
										isLoading={!closeDrawing ? isLoading : false}
									/>
								</div>
							</div>
						</div>
					)}
				</div>
			</ScrollArea>

			{fullscreen && (
				<div className="fixed inset-0 z-[999] bg-foreground-dark overflow-auto">
					<div className="relative w-full h-full flex flex-row">
						{/* toolbar */}
						<div className="flex flex-row w-[60px] shrink-0 h-full justify-end">
							<div className="relative flex flex-col items-center gap-4 p-4 h-full bg-white border-l">
								<button
									className=""
									type={"button"}
									onClick={() => {
										if (activateDrawMode || fullscreen) {
											handleCancelled(true);
										}
										setFullscreen(false);
										setCropMode(false);
										setDrawMode(false);
									}}
								>
									<IconX className="text-foreground opacity-40 hover:opacity-100" size={18} stroke={1.5} />
								</button>
								{!activateCropMode && (
									<span>
										{!activateDrawMode ? (
											<Tooltip content="Draw on the Image" side="bottom" className="z-[99]">
												<Button
													onClick={() => {
														if (!activateDrawMode) {
															setDrawMode(true);
															setCropMode(false);
														} else {
															setDrawMode(false);
														}
													}}
													type="button"
													size="icon"
													variant="ghost"
												>
													<IconWriting size={18} stroke={1.5} />
												</Button>
											</Tooltip>
										) : (
											<Tooltip content="Exit draw mode" side="bottom" className="z-[99]">
												<button
													onClick={() => {
														if (!activateDrawMode) {
															setDrawMode(true);
															setCropMode(false);
														} else {
															setDrawMode(false);
														}
													}}
													type="button"
													className="bg-danger-100 border-danger-100 text-danger-600 hover:bg-danger-100/70 hover:border-danger-100/70 h-[28px] w-[28px] text-sm rounded-md items-center justify-center inline-flex"
												>
													<IconWriting size={18} stroke={1.5} />
												</button>
											</Tooltip>
										)}
									</span>
								)}

								{!activateDrawMode && (
									<span>
										{!activateCropMode ? (
											<Tooltip content="Crop Image" side="bottom" className="z-[99]">
												<Button
													onClick={() => {
														if (!activateCropMode) {
															setCropMode(true);
															setDrawMode(false);
														} else {
															setCropMode(false);
														}
													}}
													type="button"
													size="icon"
													variant="ghost"
												>
													<IconCrop size={18} stroke={1.5} />
												</Button>
											</Tooltip>
										) : (
											<Tooltip content="Exit crop mode" side="bottom" className="z-[99]">
												<button
													onClick={() => {
														if (!activateCropMode) {
															setCropMode(true);
															setDrawMode(false);
														} else {
															setCropMode(false);
														}
													}}
													type="button"
													className="bg-danger-100 border-danger-100 text-danger-600 hover:bg-danger-100/70 hover:border-danger-100/70 h-[28px] w-[28px] text-sm rounded-md items-center justify-center inline-flex"
												>
													<IconCrop size={18} stroke={1.5} />
												</button>
											</Tooltip>
										)}
									</span>
								)}
							</div>
						</div>
						<div className="flex flex-row h-full w-full items-center justify-center">
							{!activateCropMode && !activateDrawMode && (
								<div className="flex w-full h-full justify-center">
									<img alt={alt} src={src} />
								</div>
							)}

							{activateDrawMode && (
								<div className="flex w-full h-full justify-center">
									<div
										style={{
											position: "fixed",
											top: 0,
											left: 60,
											width: "96%",
											height: "100%",
											border: "1px solid #ccc",
											backgroundColor: "#fff",
											// overflow: "hidden",
										}}
									>
										<CustomTldraw
											src={src}
											data={evidenceData}
											onImgUpdate={handleSetImage}
											onBulkEvidenceSubmit={setDraftSubmarkings}
											setIsCancelled={(value: boolean) => {
												if (value === true) {
													setDrawMode(false);
													handleCancelled(true);
												}
											}}
											newExhibit={newExhibit}
											isFullScreen={fullscreen}
											handleEvidence={handleEvidence}
											isLoading={isLoading}
										/>
									</div>
								</div>
							)}

							{activateCropMode && (
								<div className="flex">
									{!!src && (
										<ReactCrop
											crop={crop}
											onChange={(_, percentCrop) => setCrop(percentCrop)}
											onComplete={(c) => setCompletedCrop(c)}
											aspect={undefined}
											minHeight={100}
										>
											<img ref={imgRef} alt={alt} src={src} className="h-screen" />
										</ReactCrop>
									)}

									{!!completedCrop && (
										<>
											<canvas
												ref={previewCanvasRef}
												style={{
													visibility: "hidden",
													display: "none",
												}}
											/>
											<div
												className="flex flex-row justify-between"
												style={{
													position: "absolute",
													top: `${buttonPosition.top}px`,
													left: `${buttonPosition.left}px`,
													zIndex: 1000,
												}}
											>
												{/* <Button size="sm" type="button" onClick={() => handleAddEvidence("main")}>
										Add as Evidence
									</Button> */}
												<Button size="sm" type="button" onClick={() => handleAddEvidence("submarking")}>
													Add as SubMarking
												</Button>
											</div>
										</>
									)}
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
